import {ChangeDetectionStrategy, Component, computed, inject, input, InputSignal, Signal} from "@angular/core";
import {NavigationService} from "../../navigation/navigation.service";
import {OrderInitiationStatus, OrderModel} from "../order.model";
import {OrderingContextModel} from '../../ordering-context/ordering-context.model';
import {RecentOrderData} from "../recent-order.service";
import {InitiatedOrderData, PaymentSurchargeData} from "../order-initiation.service";
import {TranslateModule} from '@ngx-translate/core';
import {SpinnerComponent} from "../../ui-elements/spinner/spinner.component";
import {LinkComponent} from '../../buttons/link/link.component';
import {HtmlPipe} from '../../utils/html.pipe';
import {CurrencyPipe} from '../../utils/currency.pipe';
import {ActivatableDirective} from '../../ui-elements/activatable.directive';

@Component({
    selector: "woa-order-totals-panel",
    templateUrl: "order-totals-panel.component.html",
    styleUrl: "./order-totals-panel.component.scss",
    imports: [
        CurrencyPipe,
        HtmlPipe,
        LinkComponent,
        SpinnerComponent,
        TranslateModule,
        ActivatableDirective
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})

export class OrderTotalsPanelComponent {
    public orderingContextModel: OrderingContextModel = inject(OrderingContextModel);
    public orderModel: OrderModel = inject(OrderModel);
    private navigationService: NavigationService = inject(NavigationService);

    public readonly recentOrder: InputSignal<RecentOrderData> = input<RecentOrderData>();
    public readonly totalOnly: InputSignal<boolean> = input<boolean>(false);
    public readonly showZeroTip: InputSignal<boolean> = input<boolean>(true);
    public readonly showSavings: InputSignal<boolean> = input<boolean>(true);
    public readonly showPoints: InputSignal<boolean> = input<boolean>(true);
    public readonly canEditTip: InputSignal<boolean> = input<boolean>(true);

    OrderInitiationStatus = OrderInitiationStatus;

    public readonly initiatedOrder: Signal<InitiatedOrderData> = computed<InitiatedOrderData>(() =>
        this.recentOrder() ? this.recentOrder().initiatedOrder : this.orderModel.initiatedOrder());

    public readonly totalItems: Signal<number> = computed<number>(() =>
        this.recentOrder() ? this.recentOrder().orderInitiation.totalItems : this.orderModel.totalItems());

    public readonly tipAmount: Signal<number> = computed<number>(() =>
        !this.recentOrder() ? (this.orderModel.tipAmount() || 0)
            : !this.recentOrder().placedOrder.requestId ? this.recentOrder().initiatedOrder.tipAmount
                : this.recentOrder().orderPlacement.paymentAuthorisation.payWithPoints ? 0
                    : this.recentOrder().orderPlacement.paymentAuthorisation.tipAmount || 0);

    public readonly totalPayment: Signal<number> = computed<number>(() =>
        !this.recentOrder() ? this.orderModel.orderTotalPayment()
            : this.recentOrder().orderPlacement.paymentAuthorisation.payWithPoints ? this.recentOrder().initiatedOrder.orderTotal
                : this.recentOrder().initiatedOrder.getOrderOrderTotalWithPaymentSurcharge(PaymentSurchargeData.CREDIT_CARD_TYPE) + this.tipAmount());

    public readonly taxAmount: Signal<number> = computed<number>(() => this.initiatedOrder().taxIncluded);

    public readonly orderSaving: Signal<number> = computed<number>(() => this.recentOrder() ? 0 : this.orderModel.orderSaving());

    public readonly serviceFee: Signal<PaymentSurchargeData> = computed<PaymentSurchargeData>(() =>
        !this.recentOrder() ? this.orderModel.initiatedOrder().getPaymentSurcharge(PaymentSurchargeData.CREDIT_CARD_TYPE)
            : this.recentOrder().orderPlacement.paymentAuthorisation.payWithPoints ? undefined
                : this.recentOrder().initiatedOrder.getPaymentSurcharge(PaymentSurchargeData.CREDIT_CARD_TYPE));

    public readonly totalLabel: Signal<string> = computed(() =>
        this.orderModel.totalItems() === 0 ? undefined
            : this.orderModel.totalItems() === 1 ? "woa.label.order.totals.totalItems.singular"
                : "woa.label.order.totals.totalItems.plural");

    public readonly canShowPoints: Signal<boolean> = computed<boolean>(() =>
        this.showPoints() && !this.totalOnly() && !!this.initiatedOrder().points &&
        (!this.recentOrder() || (this.recentOrder().orderPlacement.requestId && !this.recentOrder().orderPlacement.paymentAuthorisation.payWithPoints)));

    public readonly pointsLabel: Signal<string> = computed(() =>
        this.recentOrder() ? "woa.label.order.totals.pointsEarned" : "woa.label.order.totals.earningPoints");

    async editTip(): Promise<void> {
        if (this.canEditTip()) {
            return this.navigationService.createPage("woa-tip-selection-page");
        }
    }
}
